import { useCallback, useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useParsedCohortQueryParam } from 'components/hooks/useParsedCohortQueryParam';
import { useSidebarNavMatchRoute } from 'components/hooks/useSidebarNavMatchRoute';
import { format } from 'date-fns';
import { lowerCase, startCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  // setViewsScreen,
  setViewAction,
  VIEWS_ACTIONS,
  resetView,
  resetViewsPanel,
  useViewsPanelState,
} from 'reducers/viewsSlice';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { PlusNotOutlinedWhite, Search } from 'assets/assetsV1/iconComponents';

import { getPersistenceKey } from 'constants/helper';
import { sendApplyViewEvent, sendCreateViewEvent } from 'constants/mixpanelHelpers/viewsEvents';

import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import { ChakraStyleInput } from 'uiToolkitV1/InputV2/ChakraStyleInput';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';

import { applyFilters, useViews } from '../Hooks/dataHooks';
import { useViewPageName } from '../Hooks/useViewPageName';
import { CustomHeaderComponent } from './CustomHeaderComponent';
import { ManageViewsMenu } from './ManageViewsMenu';

const StyledBox = styled(Box)`
  height: calc(100% - 8px);
  border: 4px solid ${(props) => props.theme.colors.neutral.EBEFF3};
  padding: 20px 24px 0 24px;
  margin: 0 8px 8px 8px;
  border-radius: 8px;
`;

const EmployeeRenderer = ({ data }) => (
  <ListItemComp
    title={data.employee?.metadata?.displayName}
    LeftComponent={() => <Avatar src={data.employee?.profile?.profilePhoto} />}
    isBoldText={false}
    withoutBackground
  />
);

const moduleTypeFormatter = ({ value }) => startCase(lowerCase(value)) || 'All';
const visibilityFormatter = ({ value }) => {
  if (value === 'ADMIN') {
    return 'Shared';
  }
  return startCase(lowerCase(value));
};
const dateFormatter = ({ value }) => (value ? format(new Date(value), 'MMM dd, yyyy') : '');

export const ManageViewsBody = ({ setViewSelectedInMenu }) => {
  const dispatch = useDispatch();
  const gridRef = useRef();
  const [searchText, setSearchText] = useState('');
  const { data: views } = useViews();
  const checkedRows = [];
  const { module } = useSidebarNavMatchRoute();
  const viewPage = useViewPageName();
  const queryParamsCohort = useParsedCohortQueryParam();
  const { constraints = {}, managePanelConstraints = {} } = useViewsPanelState();
  const { showMyCompanyViewsOnly = false, showSelfViewsOnly = false } = managePanelConstraints;
  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);

  const viewsFiltered = useMemo(() => {
    if (!views) return [];
    let filteredViews = views;
    if (showMyCompanyViewsOnly) {
      filteredViews = views.filter((view) => view?.baseCohort?.id === -1);
    }
    if (showSelfViewsOnly) {
      filteredViews = filteredViews.filter((view) => view?.employee?.uuid === loggedInEmployeeData.id);
    }
    return filteredViews;
  }, [loggedInEmployeeData.id, showMyCompanyViewsOnly, showSelfViewsOnly, views]);

  const history = useHistory();
  const location = useLocation();

  const gridStyle = useMemo(() => ({ height: 'calc(100% - 60px)', width: '100%' }), []);

  const columnDefs = useMemo(
    () => [
      // {
      //   field: 'isPinned',
      //   checkboxSelection: true,
      //   headerName: 'Pinned',
      //   headerTooltip: 'Pinned',
      //   maxWidth: 100,
      //   headerComponentParams: {
      //     enableMenu: false
      //   }
      // },
      {
        colId: 'name',
        field: 'name',
        headerName: 'View Name',
        headerTooltip: 'View Name',
        sortable: true,
        valueFormatter: ({ value }) => value,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
        },
        tooltipField: 'name',
      },
      {
        colId: 'moduleType',
        field: 'moduleType',
        headerName: 'View for',
        headerTooltip: 'View for',
        valueFormatter: moduleTypeFormatter,
        maxWidth: 150,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: moduleTypeFormatter,
        },
      },
      {
        colId: 'visibilityType',
        field: 'visibilityType',
        headerName: 'Visibility',
        headerTooltip: 'Visibility',
        valueFormatter: visibilityFormatter,
        maxWidth: 150,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: visibilityFormatter,
        },
      },
      {
        colId: 'createdTS',
        field: 'createdTS',
        headerName: 'Created on',
        headerTooltip: 'Created on',
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
        maxWidth: 150,
        sortable: true,
        sort: 'desc',
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
          sortDirection: 'desc',
        },
        filterParams: {
          filterOptions: ['inRange'],
        },
      },
      {
        colId: 'createdBy',
        field: 'employee',
        headerName: 'Created by',
        headerTooltip: 'Created by',
        cellRenderer: EmployeeRenderer,
        filter: 'agSetColumnFilter',
        sortable: true,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
        },
        valueGetter: ({ data }) => data.employee?.metadata?.displayName ?? '',
      },
      {
        // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
        // if you've other data in cell renderer
        colId: 'kebab-menu',
        field: '',
        maxWidth: 75,
        headerName: '',
        pinned: 'right',
        lockPosition: 'right',
        cellClass: 'locked-col',
        suppressHeaderMenuButton: true,
        cellRenderer: ManageViewsMenu,
        cellRendererParams: {
          setViewSelectedInMenu,
        },
        headerComponentParams: {
          enableMenu: false,
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      autoHeight: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      cellStyle: { height: '100%', display: 'flex', alignItems: 'center', overflow: 'hidden' },
      flex: 1,
      headerComponent: CustomHeaderComponent,
      headerComponentParams: {
        enableMenu: true,
      },
    }),
    []
  );

  const onSelectionChanged = useCallback(
    (event) => {
      //   dispatch(setCheckedRows(event.api.getSelectedRows()));
    },
    [dispatch]
  );

  const handleApply = useCallback(
    ({ data, event }) => {
      // to prevent click from 3-dot menu https://stackoverflow.com/questions/63964553/ag-grid-prevent-onrowclicked-event-when-clicking-icon-inside-cell
      if (event.defaultPrevented) {
        return;
      }

      sendApplyViewEvent({
        eventProperties: {
          source: module,
          page: viewPage,
          viewName: data.name,
          viewId: data.id,
          clickFrom: 'ManageViews',
        },
      });
      const view = { id: data.id, name: data.name, type: data.type };
      applyFilters({ history, location, view, module, dispatch, queryParamsCohort });

      // dispatch(setSelectedView(view));
      // dispatch(setViewsScreen(null));
      dispatch(resetViewsPanel());
    },
    [dispatch, resetViewsPanel, history, location, viewPage, module]
  );

  const onFirstDataRendered = useCallback((params) => {
    // gridRef.current.api.sizeColumnsToFit();
  }, []);

  const handleAction = (action) => {
    dispatch(resetView());
    dispatch(setViewAction(action));
  };

  const handleCreateNewClickWithEvent = () => {
    handleAction(VIEWS_ACTIONS.CREATE);
    sendCreateViewEvent({
      eventProperties: {
        page: viewPage,
        source: module,
        clickFrom: 'ManageViews',
      },
    });
  };

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = 'ALL';
    const viewId = null;
    const tabId = null;
    const uniqueId = 'ManageViewsGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  return (
    <StyledBox>
      <div style={gridStyle} className="ag-theme-alpine">
        <Flex alignItems="center" paddingBottom="20px" gap="16px">
          <ChakraStyleInput
            leftComponent={<Search />}
            placeholder="Search Views"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Button StartIcon={PlusNotOutlinedWhite} onClick={handleCreateNewClickWithEvent}>
            Create New
          </Button>
        </Flex>
        <AgGridWrapper
          gridStatePersistenceKey={gridStatePersistenceKey}
          ref={gridRef}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={viewsFiltered}
          animateRows
          onSelectionChanged={onSelectionChanged}
          cacheQuickFilter
          quickFilterText={searchText}
          onFirstDataRendered={onFirstDataRendered}
          rowHeight={60}
          tooltipShowDelay={500}
          onRowClicked={constraints.disableApply ? () => {} : handleApply}
          className="views"
        />
      </div>
    </StyledBox>
  );
};
